/**
 * Rich Editor Utility Functions
 * Handles spoilers, emoji, GIF search, image upload, markdown conversion
 */

/**
 * Converts Tenor GIF data to markdown image format
 */
export const getTenorImageUrl = (gif) => {
  return (
    gif?.media_formats?.gif?.url ||
    gif?.media_formats?.mediumgif?.url ||
    gif?.media_formats?.tinygif?.url ||
    gif?.media?.[0]?.gif?.url ||
    gif?.media?.[0]?.mediumgif?.url ||
    ''
  );
};

/**
 * Validates URL format and protocol
 */
export const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validates image URL format
 */
export const isValidImageUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const pathname = urlObj.pathname.toLowerCase();
    return imageExtensions.some(ext => pathname.endsWith(ext)) || !pathname;
  } catch {
    return false;
  }
};

/**
 * Returns true when a URL points to an image file.
 * Checks by file extension in the pathname and by known Hive ecosystem image CDN hostnames.
 */
export const isImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const { protocol, hostname, pathname } = new URL(url);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    // Match common image extensions in the path (even when query params follow)
    if (/\.(jpe?g|png|gif|webp|bmp|avif|tiff?|svg)(\?|$)/i.test(pathname)) return true;
    // Also check the decoded query string — covers proxy URLs (e.g. DuckDuckGo image proxy)
    // where the original image URL with its extension is passed as a query parameter.
    const { search } = new URL(url);
    if (search) {
      try {
        if (/\.(jpe?g|png|gif|webp|bmp|avif|tiff?|svg)/i.test(decodeURIComponent(search))) return true;
      } catch {
        if (/\.(jpe?g|png|gif|webp|bmp|avif|tiff?|svg)/i.test(search)) return true;
      }
    }
    // Match known Hive ecosystem image CDN hostnames that may serve images without extension
    const IMAGE_HOSTS = [
      'images.ecency.com', 'img.ecency.com',
      'images.hive.blog', 'img.hiveblocks.com',
      'steemitimages.com', 'images.steemit.com',
      'cdn.peakd.com', 'images.peakd.com',
    ];
    return IMAGE_HOSTS.some(h => hostname === h || hostname.endsWith('.' + h));
  } catch {
    return false;
  }
};

/**
 * Async image detection by response headers for extensionless URLs.
 * Best-effort only: hosts with strict CORS may block access to content-type.
 */
const imageContentProbeCache = new Map();

export const isImageUrlByContent = async (url, timeoutMs = 2500) => {
  if (!isValidUrl(url)) return false;

  const cacheKey = String(url).trim();
  if (imageContentProbeCache.has(cacheKey)) {
    return imageContentProbeCache.get(cacheKey);
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), Math.max(500, Number(timeoutMs) || 2500))
    : null;

  const getContentType = async (method) => {
    const response = await fetch(url, {
      method,
      redirect: 'follow',
      mode: 'cors',
      signal: controller?.signal,
      headers: method === 'GET' ? { Range: 'bytes=0-0' } : undefined,
    });

    if (response.type === 'opaque') return '';
    return String(response.headers.get('content-type') || '').toLowerCase();
  };

  try {
    let contentType = '';

    try {
      contentType = await getContentType('HEAD');
    } catch {
      contentType = '';
    }

    if (!contentType) {
      try {
        contentType = await getContentType('GET');
      } catch {
        contentType = '';
      }
    }

    const isImage = contentType.startsWith('image/');
    imageContentProbeCache.set(cacheKey, isImage);
    return isImage;
  } catch {
    imageContentProbeCache.set(cacheKey, false);
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const isValidVideoUrl = (url) => {
  const videoPatterns = [
    /youtube\.com|youtu\.be|vimeo\.com|twitch\.tv|dailymotion\.com|dai\.ly|(?:play\.)?3speak\.tv|d\.tube|wistia\.com|descript\.com|loom\.com/i,
  ];
  return videoPatterns.some(pattern => pattern.test(url));
};

const videoContentProbeCache = new Map();

export const isVideoUrlByContent = async (url, timeoutMs = 2500) => {
  if (!isValidUrl(url)) return false;

  const cacheKey = String(url).trim();
  if (videoContentProbeCache.has(cacheKey)) {
    return videoContentProbeCache.get(cacheKey);
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), Math.max(500, Number(timeoutMs) || 2500))
    : null;

  const getContentType = async (method) => {
    const response = await fetch(url, {
      method,
      redirect: 'follow',
      mode: 'cors',
      signal: controller?.signal,
      headers: method === 'GET' ? { Range: 'bytes=0-0' } : undefined,
    });

    if (response.type === 'opaque') return '';
    return String(response.headers.get('content-type') || '').toLowerCase();
  };

  try {
    let contentType = '';

    try {
      contentType = await getContentType('HEAD');
    } catch {
      contentType = '';
    }

    if (!contentType) {
      try {
        contentType = await getContentType('GET');
      } catch {
        contentType = '';
      }
    }

    const isVideo = contentType.startsWith('video/');
    videoContentProbeCache.set(cacheKey, isVideo);
    return isVideo;
  } catch {
    videoContentProbeCache.set(cacheKey, false);
    return false;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

export const getVideoTitleFallback = (url = '') => {
  try {
    const parsed = new URL(String(url || '').trim());
    const pathSegment = parsed.pathname
      .split('/')
      .filter(Boolean)
      .pop() || '';
    const normalized = decodeURIComponent(pathSegment)
      .replace(/[-_]+/g, ' ')
      .replace(/\.[a-z0-9]+$/i, '')
      .trim();

    if (normalized && normalized.length >= 3) {
      return normalized;
    }

    return parsed.hostname.replace(/^www\./i, '').trim();
  } catch {
    return '';
  }
};

export const fetchVideoTitleFromUrl = async (url = '') => {
  const candidate = String(url || '').trim();
  if (!candidate) return getVideoTitleFallback(candidate);

  try {
    const response = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(candidate)}`);
    if (response.ok) {
      const payload = await response.json();
      const fetched = String(payload?.title || '').trim();
      if (fetched) return fetched;
    }
  } catch {
    // Keep silent and fall back to local heuristics.
  }

  return getVideoTitleFallback(candidate);
};

export const parseHighlightShortcodeAttributes = (rawAttrs = '') => {
  const attrs = String(rawAttrs || '');
  const textColorMatch = attrs.match(/(?:^|\s)(?:text|color)\s*=\s*(?:"([^"]+)"|'([^']+)'|(rgb(?:a)?\([^)]+\)|[^\s\]]+))/i);
  const backgroundColorMatch = attrs.match(/(?:^|\s)bg\s*=\s*(?:"([^"]+)"|'([^']+)'|(rgb(?:a)?\([^)]+\)|[^\s\]]+))/i);

  return {
    textColor: (textColorMatch?.[1] || textColorMatch?.[2] || textColorMatch?.[3] || 'black').trim(),
    backgroundColor: (backgroundColorMatch?.[1] || backgroundColorMatch?.[2] || backgroundColorMatch?.[3] || 'yellow').trim(),
  };
};

const HARD_BREAK_MARKDOWN = '  \n';

const decodeShortcodeAttributeValue = (value = '') => String(value)
  .replace(/&#93;/g, ']')
  .replace(/\\"/g, '"')
  .replace(/\\\\/g, '\\');

const encodeShortcodeAttributeValue = (value = '') => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"')
  .replace(/\]/g, '&#93;');

const escapeHtmlAttr = (value = '') => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/"/g, '&quot;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const escapeHtmlText = (value = '') => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const normalizeShortcodeAlignment = (value = 'left') => {
  const normalized = String(value || 'left').toLowerCase();
  if (normalized === 'center' || normalized === 'right') return normalized;
  return 'left';
};

const getShortcodeAttribute = (rawAttrs = '', name = '') => {
  const key = String(name || '').trim();
  if (!key) return '';
  const attrs = String(rawAttrs || '');
  const match = attrs.match(new RegExp(`(?:^|\\s)${key}\\s*=\\s*(?:"((?:\\\\.|[^"])*)"|'((?:\\\\.|[^'])*)'|([^\\s\\]]+))`, 'i'));
  return decodeShortcodeAttributeValue(match?.[1] || match?.[2] || match?.[3] || '').trim();
};

const getShortcodeAlignmentFromAttrs = (rawAttrs = '') => (
  normalizeShortcodeAlignment(getShortcodeAttribute(rawAttrs, 'align') || 'left')
);

const getHtmlAlignmentFromTag = (tag = '') => {
  const source = String(tag || '');
  const explicitShortappAlign = source.match(/\sdata-shortapp-align\s*=\s*(?:"(left|center|right)"|'(left|center|right)')/i);
  const dataAlign = source.match(/\sdata-align\s*=\s*(?:"(left|center|right)"|'(left|center|right)')/i);
  const alignAttr = source.match(/\salign\s*=\s*(?:"(left|center|right)"|'(left|center|right)')/i);
  const classAlign = source.match(/\btext-(left|center|right)\b/i)?.[1];
  const styleAlign = source.match(/text-align\s*:\s*(left|center|right)/i)?.[1];
  const explicitAlign = normalizeShortcodeAlignment(
    explicitShortappAlign?.[1]
      || explicitShortappAlign?.[2]
      || dataAlign?.[1]
      || dataAlign?.[2]
      || alignAttr?.[1]
      || alignAttr?.[2]
      || classAlign
      || styleAlign
      || ''
  );
  if (explicitAlign !== 'left') return explicitAlign;

  const styleText = source.match(/style\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
  const style = String(styleText?.[1] || styleText?.[2] || '').toLowerCase();
  const marginShorthand = style.match(/margin\s*:\s*([^;]+)/i)?.[1] || '';
  if (marginShorthand) {
    const parts = marginShorthand.trim().split(/\s+/).map((part) => part.toLowerCase());
    const rightToken = parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? parts[1]
        : parts.length === 3
          ? parts[1]
          : parts[1];
    const leftToken = parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? parts[1]
        : parts.length === 3
          ? parts[1]
          : parts[3];

    if (leftToken === 'auto' && rightToken === 'auto') return 'center';
    if (leftToken === 'auto' && rightToken !== 'auto') return 'right';
    if (rightToken === 'auto' && leftToken !== 'auto') return 'left';
  }

  return 'left';
};

const buildAlignShortcodeAttr = (alignment = 'left') => {
  const resolved = normalizeShortcodeAlignment(alignment);
  return ` align="${resolved}"`;
};

export const buildCanonicalImageShortcode = (url = '', alignment = 'left') => {
  const imageUrl = String(url || '').trim();
  if (!imageUrl) return '';
  const canonical = `[img url="${encodeShortcodeAttributeValue(imageUrl)}"]`;
  const resolvedAlignment = normalizeShortcodeAlignment(alignment || 'left');
  if (resolvedAlignment === 'left') return canonical;
  return `[img url="${encodeShortcodeAttributeValue(imageUrl)}" align="${resolvedAlignment}"]`;
};

const buildCanonicalVideoShortcode = (url = '', title = '', alignment = 'left') => {
  const videoUrl = String(url || '').trim();
  if (!videoUrl) return '';
  const titleAttribute = title ? ` title="${encodeShortcodeAttributeValue(title)}"` : '';
  const resolvedAlignment = normalizeShortcodeAlignment(alignment || 'left');
  const alignAttribute = resolvedAlignment !== 'left' ? ` align="${resolvedAlignment}"` : '';
  return `[video url="${encodeShortcodeAttributeValue(videoUrl)}"${titleAttribute}${alignAttribute}]`;
};

const getYouTubeIdFromUrl = (url = '') => {
  const source = String(url || '').trim();
  if (!source) return '';
  try {
    const parsed = new URL(source);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();

    if (host === 'youtu.be') {
      return parsed.pathname.split('/').filter(Boolean)[0] || '';
    }

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      const watchId = parsed.searchParams.get('v');
      if (watchId) return watchId;

      const parts = parsed.pathname.split('/').filter(Boolean);
      const embedIndex = parts.findIndex((p) => p === 'embed' || p === 'shorts' || p === 'live');
      if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1];
    }
  } catch {
    return '';
  }

  return '';
};

export const getVideoEmbedUrl = (url = '') => {
  const source = String(url || '').trim();
  if (!source) return '';

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();

    const youtubeId = getYouTubeIdFromUrl(source);
    if (youtubeId) {
      return `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}`;
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const parts = parsed.pathname.split('/').filter(Boolean);
      const id = parts[parts.length - 1];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }

    if (host === 'dailymotion.com' || host === 'dai.ly') {
      if (host === 'dai.ly') {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        if (id) return `https://www.dailymotion.com/embed/video/${id}`;
      }
      const match = parsed.pathname.match(/\/video\/([\w-]+)/i);
      if (match?.[1]) return `https://www.dailymotion.com/embed/video/${match[1]}`;
    }

    if (host === '3speak.tv' || host === 'play.3speak.tv') {
      const value = parsed.searchParams.get('v') || '';
      if (value) {
        const safeId = String(value).trim().replace(/%2F/gi, '/');
        return `https://play.3speak.tv/watch?v=${safeId}&mode=iframe&layout=desktop`;
      }
    }

    if (host === 'twitch.tv' || host.endsWith('.twitch.tv')) {
      const match = parsed.pathname.match(/\/videos\/(\d+)/i);
      if (match?.[1]) {
        const parent = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        return `https://player.twitch.tv/?video=v${match[1]}&parent=${encodeURIComponent(parent)}`;
      }
    }

    if (host === 'loom.com' || host.endsWith('.loom.com')) {
      const match = parsed.pathname.match(/\/share\/([a-zA-Z0-9]+)/i);
      if (match?.[1]) return `https://www.loom.com/embed/${match[1]}`;
    }
  } catch {
    return '';
  }

  return '';
};

const htmlToPlainText = (html = '') => String(html || '')
  .replace(/<img[^>]*>/gi, ' ')
  .replace(/<iframe[^>]*><\/iframe>/gi, ' ')
  .replace(/<iframe[^>]*>/gi, ' ')
  .replace(/<br\s*\/?\s*>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getHtmlAttributeValue = (source = '', name = '') => {
  const key = String(name || '').trim();
  if (!key) return '';
  const match = String(source || '').match(new RegExp(`${key}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i'));
  return decodeShortcodeAttributeValue(String(match?.[1] || match?.[2] || '').trim());
};

const buildEditorVideoPreviewHtml = (url = '', title = '', alignment = 'left') => {
  const videoUrl = String(url || '').trim();
  if (!videoUrl) return '';

  const resolvedAlignment = normalizeShortcodeAlignment(alignment || 'left');
  const cleanTitle = String(title || '').trim();
  const label = cleanTitle || videoUrl;
  const embedUrl = getVideoEmbedUrl(videoUrl);

  if (!embedUrl) {
    return `<p><a href="${escapeHtmlAttr(videoUrl)}" target="_blank" rel="noopener noreferrer" class="shortapp-editor-video-link">${escapeHtmlText(label)}</a></p>`;
  }

  return `<iframe src="${escapeHtmlAttr(embedUrl)}" class="shortapp-editor-video-embed" data-shortapp-video-embed="1" data-shortapp-video-url="${escapeHtmlAttr(videoUrl)}" data-shortapp-video-title="${escapeHtmlAttr(cleanTitle)}" data-shortapp-align="${resolvedAlignment}" data-align="${resolvedAlignment}" frameborder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
};

const getImageAlignmentStyle = (alignment = 'left') => {
  const resolved = normalizeShortcodeAlignment(alignment || 'left');
  if (resolved === 'center') return 'display: block; margin: 8px auto;';
  if (resolved === 'right') return 'display: block; margin: 8px 0 8px auto;';
  return 'display: block; margin: 8px auto 8px 0;';
};

const alignImagesWithinHtml = (html = '', alignment = 'left') => {
  const resolved = normalizeShortcodeAlignment(alignment || 'left');
  return String(html || '').replace(/<img\b([^>]*)>/gi, (match, attrs = '') => {
    let normalizedAttrs = String(attrs || '')
      .replace(/\s(?:align|data-align|data-shortapp-align)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\sclass\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .replace(/\sstyle\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .trim();

    normalizedAttrs = normalizedAttrs ? ` ${normalizedAttrs}` : '';
    return `<img${normalizedAttrs} class="text-${resolved}" align="${resolved}" data-align="${resolved}" data-shortapp-align="${resolved}" style="${getImageAlignmentStyle(resolved)}" />`;
  });
};

/**
 * Converts HTML to markdown-compatible format
 */
export const htmlToMarkdown = (html, format = 'markdown') => {
  if (!html) return '';

  const source = String(html);
  const hasImage = /<img\b[^>]*>/i.test(source);
  const hasVideo = /<iframe\b[^>]*data-shortapp-video-embed\s*=\s*(?:"1"|'1')[^>]*>|<a\b[^>]*class="[^"]*\bshortapp-editor-video-link\b[^"]*"[^>]*>/i.test(source);
  const hasTrailingEmptyParagraphAfterImage = hasImage
    && /<p[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?\s*>)*<\/p>\s*$/i.test(source);
  const hasTrailingBreaksAfterImage = hasImage
    && /(?:<br\s*\/?\s*>\s*){2,}$/i.test(source);
  const hasTrailingEmptyParagraphAfterVideo = hasVideo
    && /<p[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?\s*>)*<\/p>\s*$/i.test(source);
  const hasTrailingBreaksAfterVideo = hasVideo
    && /(?:<br\s*\/?\s*>\s*){2,}$/i.test(source);
  if (!/<\/?[a-z][\s\S]*>/i.test(source.trim())) {
    return source.trim();
  }
  
  let markdown = source;

  // Preserve text alignment from WYSIWYG output as shortcode for reliable round-trip.
  // Skip 'left' since it's the default alignment and doesn't need to be encoded.
  markdown = markdown.replace(
    /<(p|div|h[1-6])[^>]*style="[^"]*text-align\s*:\s*(center|right)[^"]*"[^>]*>([\s\S]*?)<\/\1>/gi,
    (_m, _tag, align, inner) => `\n[align=${String(align).toLowerCase()}]${inner}[/align]\n`
  );
  markdown = markdown.replace(
    /<(p|div|h[1-6])[^>]*align="(center|right)"[^>]*>([\s\S]*?)<\/\1>/gi,
    (_m, _tag, align, inner) => `\n[align=${String(align).toLowerCase()}]${inner}[/align]\n`
  );
  markdown = markdown.replace(
    /<(p|div|h[1-6])[^>]*class="[^"]*\btext-(center|right)\b[^"]*"[^>]*>([\s\S]*?)<\/\1>/gi,
    (_m, _tag, align, inner) => `\n[align=${String(align).toLowerCase()}]${inner}[/align]\n`
  );
  
  if (format === 'shortcode') {
    markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n[h1]$1[/h1]\n');
    markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n[h2]$1[/h2]\n');
    markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n[h3]$1[/h3]\n');
    markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '\n[h4]$1[/h4]\n');
    markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '\n[h5]$1[/h5]\n');
    markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '[b]$1[/b]');
    markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '[b]$1[/b]');
    markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '[i]$1[/i]');
    markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '[i]$1[/i]');
    markdown = markdown.replace(/<u[^>]*>(.*?)<\/u>/gi, '[u]$1[/u]');
    markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, '[s]$1[/s]');
    markdown = markdown.replace(/<strike[^>]*>(.*?)<\/strike>/gi, '[s]$1[/s]');
  } else {
    markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n# $1\n');
    markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n## $1\n');
    markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n### $1\n');
    markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '\n#### $1\n');
    markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '\n##### $1\n');
    markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
    markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
    markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
    markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
    markdown = markdown.replace(/<u[^>]*>(.*?)<\/u>/gi, '<u>$1</u>');
    markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~');
    markdown = markdown.replace(/<strike[^>]*>(.*?)<\/strike>/gi, '~~$1~~');
  }
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');
  markdown = markdown.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, '\n```\n$1\n```\n');
  markdown = markdown.replace(/<div[^>]*data-shortapp-video\s*=\s*(?:"1"|'1')[^>]*data-shortapp-video-url\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([\s\S]*?)<\/div>/gi, (match, urlA, urlB) => {
    const url = decodeShortcodeAttributeValue(String(urlA || urlB || '').trim());
    if (!url) return '';
    const titleMatch = String(match).match(/data-shortapp-video-title\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
    const title = decodeShortcodeAttributeValue(String(titleMatch?.[1] || titleMatch?.[2] || '').trim());
    const align = getHtmlAlignmentFromTag(match);
    return buildCanonicalVideoShortcode(url, title, align);
  });

  markdown = markdown.replace(/<iframe[^>]*data-shortapp-video-embed\s*=\s*(?:"1"|'1')[^>]*>/gi, (tag) => {
    const url = getHtmlAttributeValue(tag, 'data-shortapp-video-url')
      || getHtmlAttributeValue(tag, 'data-video-url')
      || getHtmlAttributeValue(tag, 'src');
    const title = getHtmlAttributeValue(tag, 'data-shortapp-video-title');
    if (!url) return '';
    const align = getHtmlAlignmentFromTag(tag);
    return buildCanonicalVideoShortcode(url, title, align);
  });

  // Primary resilient path: link class survives TipTap normalization.
  markdown = markdown.replace(/<a[^>]*class="[^"]*\bshortapp-editor-video-link\b[^"]*"[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href, body) => {
    const url = String(href || '').trim();
    const title = htmlToPlainText(body);
    if (!url) return '';
    if (!isValidVideoUrl(url)) {
      return `[link url="${url}"]${title || url}[/link]`;
    }
    return buildCanonicalVideoShortcode(url, title, 'left');
  });

  // Fallback: if ProseMirror stripped video wrapper attributes, keep editor video
  // preview rows from degrading into [link] by catching icon + anchor patterns.
  markdown = markdown.replace(/<span[^>]*>\s*▶\s*<\/span>\s*<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href, body) => {
    const url = String(href || '').trim();
    const title = String(body || '').trim();
    if (!isValidVideoUrl(url)) {
      return `[link url="${url}"]${title || url}[/link]`;
    }
    return buildCanonicalVideoShortcode(url, title, 'left');
  });
  markdown = markdown.replace(/▶\s*<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href, body) => {
    const url = String(href || '').trim();
    const title = String(body || '').trim();
    if (!isValidVideoUrl(url)) {
      return `[link url="${url}"]${title || url}[/link]`;
    }
    return buildCanonicalVideoShortcode(url, title, 'left');
  });

  markdown = markdown.replace(/<a[^>]*data-type="mention"[^>]*>([\s\S]*?)<\/a>/gi, (_m, body) => {
    const text = String(body || '').trim();
    return text.startsWith('@') ? text : `@${text}`;
  });

  // Keep user-profile mention links as plain @username in authored markdown.
  // These links should be created at render time, not baked into saved content.
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_match, href, body) => {
    const rawHref = String(href || '').trim();
    const text = String(body || '').trim();
    const mentionMatch = text.match(/^@([a-z0-9._-]+)$/i);
    const hrefMatch = rawHref.match(/^\/?(?:user\/|@)([a-z0-9._-]+)$/i);
    if (mentionMatch && hrefMatch) {
      return `@${mentionMatch[1]}`;
    }
    return _match;
  });

  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_match, href, body) => {
    const text = String(body || '').trim() || String(href || '').trim();
    return `[link url="${String(href || '').trim()}"]${text}[/link]`;
  });
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, (match, src) => {
    const align = getHtmlAlignmentFromTag(match);
    return buildCanonicalImageShortcode(src, align);
  });
  markdown = markdown.replace(/<span[^>]*style="([^"]*)"[^>]*>([\s\S]*?)<\/span>/gi, (_m, styleText, inner) => {
    const style = String(styleText || '');
    const colorMatch = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
    const bgMatch = style.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)/i);
    if (!colorMatch && !bgMatch) return `<span style="${style}">${inner}</span>`;
    let color = (colorMatch?.[1] || 'black').trim();
    let bg = (bgMatch?.[1] || 'yellow').trim();
    
    // Convert rgb to hex to save blockchain bytes
    const rgbToHex = (str) => {
      const match = str.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
      if (match) {
        return '#' + match.slice(1).map(x => parseInt(x, 10).toString(16).padStart(2, '0')).join('');
      }
      return str;
    };
    
    color = rgbToHex(color);
    bg = rgbToHex(bg);

    return `[highlight color="${color}" bg="${bg}"]${inner}[/highlight]`;
  });
  markdown = markdown.replace(/<hr\s*\/?>/gi, '\n---\n\n');
  markdown = markdown.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (match, inner) => {
    const align = getHtmlAlignmentFromTag(match);
    const text = String(inner || '')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/p>/gi, '\n')
      // Store Shift+Enter as markdown hard-break syntax for Hive compatibility.
      .replace(/<br\s*\/?>/gi, HARD_BREAK_MARKDOWN)
      .replace(/<[^>]+>/g, '')
      .trim();

    if (!text) return '';
    return `\n[blockquote${buildAlignShortcodeAttr(align)}]${text}[/blockquote]\n`;
  });
  // Keep Shift+Enter round-trip as markdown hard-break syntax globally.
  markdown = markdown.replace(/<br\s*\/?>/gi, HARD_BREAK_MARKDOWN);
  markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');
  // Ordered lists: numbered items, strip inner <p> tags
  markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
    let i = 0;
    return '\n' + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, itemContent) => {
      i += 1;
      const text = itemContent
        .replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1 ')
        .replace(/<[^>]+>/g, '')
        .trim();
      return `${i}. ${text}\n`;
    });
  });
  // Unordered lists: bullet items, strip inner <p> tags
  markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner) => {
    return '\n' + inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, itemContent) => {
      const text = itemContent
        .replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1 ')
        .replace(/<[^>]+>/g, '')
        .trim();
      return `- ${text}\n`;
    });
  });
  // Fallback: any remaining bare <li> not inside a processed list
  markdown = markdown.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n');
  markdown = markdown.replace(/<[^>]+>/g, '');
  markdown = markdown.replace(/\[align=(left|center|right)\]\s*(\[img\s+[^\]]+\])\s*\[\/align\]/gi, (_m, align, imageShortcode) => {
    const attrsMatch = imageShortcode.match(/^\[img\s+([^\]]+)\]$/i);
    const rawAttrs = attrsMatch?.[1] || '';
    const url = getShortcodeAttribute(rawAttrs, 'url') || getShortcodeAttribute(rawAttrs, 'src');
    if (!url) return imageShortcode;
    return buildCanonicalImageShortcode(url, align);
  });
  markdown = markdown.replace(/\[align=(left|center|right)\]\s*(\[video\s+[^\]]+\])\s*\[\/align\]/gi, (_m, align, videoShortcode) => {
    const attrsMatch = videoShortcode.match(/^\[video\s+([^\]]+)\]$/i);
    const rawAttrs = attrsMatch?.[1] || '';
    const url = getShortcodeAttribute(rawAttrs, 'url') || getShortcodeAttribute(rawAttrs, 'src');
    const title = getShortcodeAttribute(rawAttrs, 'title');
    if (!url) return videoShortcode;
    return buildCanonicalVideoShortcode(url, title, align);
  });
  let previousMarkdown;
  do {
    previousMarkdown = markdown;
    markdown = markdown.replace(/\[align=(left|center|right)\]\s*\[align=\1\]([\s\S]*?)\[\/align\]\s*\[\/align\]/gi, '[align=$1]$2[/align]');
  } while (markdown !== previousMarkdown);
  markdown = markdown.replace(/\n{2,}/g, (match) => {
    const pairs = Math.floor(match.length / 2);
    if (pairs <= 1) return '\n\n';
    // Each extra paragraph break (beyond the first) becomes an explicit <br>
    // so that multiple blank lines entered in the editor are preserved visually.
    return '\n\n' + '<br>\n\n'.repeat(pairs - 1);
  });
  markdown = markdown.trim();

  // Final safeguard: normalize any residual editor artifact like
  // "▶[link url=... ]Title[/link]" back into canonical [video] shortcode.
  markdown = markdown.replace(/▶\s*\[link\s+([^\]]+)\]([\s\S]*?)\[\/link\]/gi, (_m, rawAttrs = '', body = '') => {
    const url = getShortcodeAttribute(rawAttrs, 'url') || getShortcodeAttribute(rawAttrs, 'href');
    if (!url || !isValidVideoUrl(url)) {
      return `[link ${rawAttrs}]${String(body || '').trim()}[/link]`;
    }
    const title = String(body || '').trim();
    return buildCanonicalVideoShortcode(url, title, 'left');
  });
  
  // Ensure trailing newlines after image shortcodes
  if ((hasTrailingEmptyParagraphAfterImage || hasTrailingBreaksAfterImage) && !markdown.endsWith('\n\n')) {
    markdown = `${markdown}\n\n`;
  }

  // Ensure trailing newlines after video shortcodes
  if ((hasTrailingEmptyParagraphAfterVideo || hasTrailingBreaksAfterVideo) && !markdown.endsWith('\n\n')) {
    markdown = `${markdown}\n\n`;
  }
  
  // Fallback: if markdown ends with an img shortcode without trailing newlines, add them
  if (/\[img\s+[^\]]+\]$/.test(markdown) && !markdown.endsWith('\n\n')) {
    markdown = `${markdown}\n\n`;
  }

  // Fallback: if markdown ends with a video shortcode without trailing newlines, add them
  if (/\[video\s+[^\]]+\]$/.test(markdown) && !markdown.endsWith('\n\n')) {
    markdown = `${markdown}\n\n`;
  }
  
  // Decode HTML entities back to plain characters so the blockchain
  // receives > and < rather than &gt; / &lt;. Order matters: decode
  // &gt; and &lt; before &amp; so that &amp;gt; → &gt; (not →>).
  markdown = markdown.replace(/&gt;/g, '>');
  markdown = markdown.replace(/&lt;/g, '<');
  markdown = markdown.replace(/&amp;/g, '&');
  
  return markdown;
};

/**
 * Converts markdown to HTML for TipTap editor
 */
export const normalizeAlignmentHtml = (input) => {
  if (!input) return '';

  let normalized = String(input).replace(
    /<div\s+align\s*=\s*"(left|center|right)"\s*>([\s\S]*?)<\/div>/gi,
    (_m, align, inner) => {
      const resolved = String(align).toLowerCase();
      return `<div class="text-${resolved}" align="${resolved}" style="text-align: ${resolved};">${inner}</div>`;
    }
  );

  normalized = normalized.replace(
    /<div\s+class\s*=\s*"[^"]*\btext-(left|center|right)\b[^"]*"\s*>([\s\S]*?)<\/div>/gi,
    (_m, align, inner) => {
      const resolved = String(align).toLowerCase();
      return `<div class="text-${resolved}" align="${resolved}" style="text-align: ${resolved};">${inner}</div>`;
    }
  );

  return normalized;
};

export const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Escape HTML special chars FIRST so injected tags from shortcodes aren't affected
  html = html.replace(/&/g, '&amp;');
  html = html.replace(/</g, '&lt;');
  html = html.replace(/>/g, '&gt;');
  
  // Convert blockquote markdown using the now-escaped prefix (&gt; ).
  // Spoiler syntax (&gt;!) and lone &gt; won't match because we require a space + content.
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');
  html = html.replace(/^\s*-{3,}\s*$/gm, '<hr />');
  
  html = html.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*?)$/gm, '<h1>$1</h1>');
  html = html.replace(/\[hl(\s+[^\]]*)?\]([\s\S]*?)\[\/hl\]/gi, '[highlight$1]$2[/highlight]');
  html = html.replace(/\[h1\]([\s\S]*?)\[\/h1\]/gi, '<h1>$1</h1>');
  html = html.replace(/\[h2\]([\s\S]*?)\[\/h2\]/gi, '<h2>$1</h2>');
  html = html.replace(/\[h3\]([\s\S]*?)\[\/h3\]/gi, '<h3>$1</h3>');
  html = html.replace(/\[h4\]([\s\S]*?)\[\/h4\]/gi, '<h4>$1</h4>');
  html = html.replace(/\[h5\]([\s\S]*?)\[\/h5\]/gi, '<h5>$1</h5>');
  html = html.replace(/\[b\]([\s\S]*?)\[\/b\]/gi, '<strong>$1</strong>');
  html = html.replace(/\[i\]([\s\S]*?)\[\/i\]/gi, '<em>$1</em>');
  html = html.replace(/\[u\]([\s\S]*?)\[\/u\]/gi, '<u>$1</u>');
  html = html.replace(/\[s\]([\s\S]*?)\[\/s\]/gi, '<s>$1</s>');
  html = html.replace(/(^|\s|>|>)@([a-z0-9.-]+)\b/gi, '$1<a href="/user/$2" class="mention" data-type="mention" data-id="$2" target="_blank" rel="noopener noreferrer">@$2</a>');
  html = html.replace(/\[blockquote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/blockquote\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const align = getShortcodeAlignmentFromAttrs(rawAttrs);
    const content = String(bodyText || '')
      .replace(/&lt;br\s*\/?&gt;/gi, '<br />')
      .replace(/\r?\n/g, '<br />');
    return `<blockquote class="text-${align}" align="${align}" data-align="${align}" style="text-align: ${align};">${content}</blockquote>`;
  });
  html = html.replace(/\[quote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/quote\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const align = getShortcodeAlignmentFromAttrs(rawAttrs);
    const content = String(bodyText || '')
      .replace(/&lt;br\s*\/?&gt;/gi, '<br />')
      .replace(/\r?\n/g, '<br />');
    return `<blockquote class="text-${align}" align="${align}" data-align="${align}" style="text-align: ${align};">${content}</blockquote>`;
  });
  html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/~~(.*?)~~/g, '<s>$1</s>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  html = html.replace(/\[align=(left|center|right)\]\s*\[img\s+([^\]]+)\]\s*\[\/align\]/gi, (_m, alignValue, attrs) => {
    const src = getShortcodeAttribute(attrs, 'url') || getShortcodeAttribute(attrs, 'src');
    const cleaned = String(src || '').trim();
    if (!cleaned) return '';
    const align = normalizeShortcodeAlignment(alignValue || 'left');
    return `<img src="${cleaned}" alt="" data-shortapp-align="${align}" data-align="${align}" align="${align}" class="text-${align}" style="${getImageAlignmentStyle(align)}" />`;
  });
  html = html.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" />');
  html = html.replace(/\[link\s+([^\]]+)\]([\s\S]*?)\[\/link\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const url = getShortcodeAttribute(rawAttrs, 'url') || getShortcodeAttribute(rawAttrs, 'href');
    const label = String(bodyText || '').trim() || String(url || '').trim();
    if (!url) return label;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');
  html = html.replace(/\[img\s+([^\]]+)\]/gi, (_m, attrs) => {
    const src = getShortcodeAttribute(attrs, 'url') || getShortcodeAttribute(attrs, 'src');
    const align = getShortcodeAlignmentFromAttrs(attrs);
    const cleaned = String(src || '').trim();
    if (!cleaned) return '';
    if (align === 'left') return `<img src="${cleaned}" alt="" data-shortapp-align="left" />`;
    return `<img src="${cleaned}" alt="" data-shortapp-align="${align}" data-align="${align}" align="${align}" class="text-${align}" style="${getImageAlignmentStyle(align)}" />`;
  });
  html = html.replace(/\[highlight(?:\s+([^\]]+))?\]([\s\S]*?)\[\/highlight\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const { textColor, backgroundColor } = parseHighlightShortcodeAttributes(rawAttrs);
    return `<span style="background-color: ${backgroundColor}; color: ${textColor}; padding-left: 2px; padding-right: 2px;">${bodyText}</span>`;
  });
  html = html.replace(/\[video\s+([^\]]+)\]/gi, (_m, rawAttrs = '') => {
    const url = getShortcodeAttribute(rawAttrs, 'url') || getShortcodeAttribute(rawAttrs, 'src');
    if (!url) return '';
    const title = getShortcodeAttribute(rawAttrs, 'title');
    const align = getShortcodeAlignmentFromAttrs(rawAttrs);
    return buildEditorVideoPreviewHtml(url, title, align);
  });
  html = html.replace(/\[spoiler(?:\s+([^\]]+))?\]([\s\S]*?)\[\/spoiler\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const title = getShortcodeAttribute(rawAttrs, 'title') || 'Spoiler';
    const align = getShortcodeAlignmentFromAttrs(rawAttrs);
    const content = String(bodyText || '').trim() || 'Hidden content';
    return `<div class="text-${align}" align="${align}" style="text-align: ${align};"><details class="markdown-spoiler" data-align="${align}"><summary>${title}</summary><div>${content}</div></details></div>`;
  });
  html = html.replace(
    /\[align=(left|center|right)\]([\s\S]*?)\[\/align\]/gi,
    (_m, align, inner) => {
      const resolved = String(align).toLowerCase();
      const innerWithAlignedImages = alignImagesWithinHtml(inner, resolved);
      return `<div class="text-${resolved}" align="${resolved}" style="text-align: ${resolved};">${innerWithAlignedImages}</div>`;
    }
  );
  html = html.replace(/\n/g, '<br />');
  // Replace <br /><br /> immediately following an img with an empty paragraph
  // so TipTap (block image node) renders a proper empty line after the image.
  html = html.replace(/(<img\b[^>]*\/>)(?:<br \/>){2,}/gi, '$1<p></p>');

  return normalizeAlignmentHtml(html);
};

/**
 * Extracts plain text from HTML content
 */
export const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

/**
 * Converts ShortApp shortcodes to standard markdown equivalents.
 * Used when posting to non-flex feeds so other Hive clients render the content
 * correctly. Shortcodes without a markdown equivalent are either stripped
 * ([align] → inner text only) or preserved ([spoiler] → kept as-is).
 */
export const shortcodesToMarkdown = (text) => {
  if (!text) return text;
  let out = text;

  out = out.replace(/\[hl(\s+[^\]]*)?\]([\s\S]*?)\[\/hl\]/gi, '[highlight$1]$2[/highlight]');

  // Headings — must sit on their own line for markdown parsers
  out = out.replace(/\[h1\]([\s\S]*?)\[\/h1\]/gi, '\n# $1\n');
  out = out.replace(/\[h2\]([\s\S]*?)\[\/h2\]/gi, '\n## $1\n');
  out = out.replace(/\[h3\]([\s\S]*?)\[\/h3\]/gi, '\n### $1\n');
  out = out.replace(/\[h4\]([\s\S]*?)\[\/h4\]/gi, '\n#### $1\n');
  out = out.replace(/\[h5\]([\s\S]*?)\[\/h5\]/gi, '\n##### $1\n');

  // Inline text formatting
  out = out.replace(/\[b\]([\s\S]*?)\[\/b\]/gi, '**$1**');
  out = out.replace(/\[i\]([\s\S]*?)\[\/i\]/gi, '*$1*');
  out = out.replace(/\[s\]([\s\S]*?)\[\/s\]/gi, '~~$1~~');
  out = out.replace(/\[u\]([\s\S]*?)\[\/u\]/gi, '<u>$1</u>');

  // Blockquotes — prefix each line with > (standard markdown)
  const prefixBlockquote = (inner) =>
    String(inner || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .trim()
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');
  out = out.replace(/\[blockquote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/blockquote\]/gi, (_m, rawAttrs = '', inner) => {
    const aligned = getShortcodeAlignmentFromAttrs(rawAttrs);
    const content = prefixBlockquote(inner);
    if (aligned === 'left') return content;
    return `<div style="text-align: ${aligned};">${content}</div>`;
  });
  out = out.replace(/\[quote(?:\s+([^\]]+))?\]([\s\S]*?)\[\/quote\]/gi, (_m, rawAttrs = '', inner) => {
    const aligned = getShortcodeAlignmentFromAttrs(rawAttrs);
    const content = prefixBlockquote(inner);
    if (aligned === 'left') return content;
    return `<div style="text-align: ${aligned};">${content}</div>`;
  });

  // Alignment — no markdown equivalent; strip the shortcode tags, keep inner content
  out = out.replace(/\[align=(left|center|right)\]([\s\S]*?)\[\/align\]/gi, (_m, align, inner) => {
    const a = String(align || 'left').toLowerCase();
    if (a === 'left') return inner;
    return `<div style="text-align: ${a};">${inner}</div>`;
  });

  // Video — emit the bare URL on its own line so clients that support it will
  // auto-embed (PeakD, Ecency, etc.).  Title is discarded; no standard equivalent.
  out = out.replace(/\[video\s+([^\]]+)\]/gi, (_m, attrs) => {
    const url = getShortcodeAttribute(attrs, 'url') || getShortcodeAttribute(attrs, 'src');
    const aligned = getShortcodeAlignmentFromAttrs(attrs);
    if (!url) return '';
    if (aligned === 'left') return `\n${url}\n`;
    return `\n<div style="text-align: ${aligned};">${url}</div>\n`;
  });

  out = out.replace(/\[link\s+([^\]]+)\]([\s\S]*?)\[\/link\]/gi, (_m, attrs = '', bodyText = '') => {
    const href = getShortcodeAttribute(attrs, 'url') || getShortcodeAttribute(attrs, 'href');
    const label = String(bodyText || '').trim() || String(href || '').trim();
    if (!href) return label;
    return `[${label}](${href})`;
  });

  // Image shortcode — convert to <img> tags for reliable rendering across clients.
  // Left-aligned uses a plain <img>; center/right wraps in a styled <div>.
  out = out.replace(/\[img\s+([^\]]+)\]/gi, (_m, attrs) => {
    const url = getShortcodeAttribute(attrs, 'url') || getShortcodeAttribute(attrs, 'src');
    const aligned = getShortcodeAlignmentFromAttrs(attrs);
    const cleaned = String(url || '').trim();
    if (!cleaned) return '';
    if (aligned === 'left') return `\n<img src="${cleaned}" />\n`;
    return `\n<div style="text-align: ${aligned};"><img src="${cleaned}" /></div>\n`;
  });

  out = out.replace(/\[highlight(?:\s+([^\]]+))?\]([\s\S]*?)\[\/highlight\]/gi, (_m, rawAttrs = '', bodyText = '') => {
    const { textColor, backgroundColor } = parseHighlightShortcodeAttributes(rawAttrs);
    const textValue = String(bodyText || '').trim();
    if (!textValue) return '';
    return `<span style="background-color: ${backgroundColor}; color: ${textColor}; padding-left: 2px; padding-right: 2px;">${textValue}</span>`;
  });

  // [spoiler] has no Hive-standard equivalent — leave it as-is

  // Clean up excessive blank lines introduced by the replacements above
  out = out.replace(/\n{3,}/g, '\n\n').trim();

  return out;
};

/**
 * Count characters in content (for character limits)
 */
export const countCharacters = (html) => {
  const text = stripHtml(html);
  return text.replace(/\s+/g, ' ').trim().length;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Convert File to base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Fetches Tenor GIFs based on search query
 */
export const searchTenorGifs = async (query, limit = 20) => {
  const fallbackKey = 'LIVDSRZULELA';
  const apiKey = process.env.REACT_APP_TENOR_API_KEY || fallbackKey;
  const normalizedQuery = String(query || '').trim();
  const endpoint = normalizedQuery ? 'search' : 'featured';
  const queryPart = normalizedQuery ? `q=${encodeURIComponent(normalizedQuery)}&` : '';
  
  try {
    const response = await fetch(
      `https://tenor.googleapis.com/v2/${endpoint}?${queryPart}key=${apiKey}&client_key=shortapp&limit=${limit}&media_filter=gif`
    );
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    const results = data.results || [];
    if (results.length > 0) return results;

    // Fallback local set so GIF UI never appears empty.
    const fallback = [
      'https://media.tenor.com/8M0Kx7Q4GxAAAAAC/happy-dance.gif',
      'https://media.tenor.com/bm8Q6yAlsPsAAAAC/thumbs-up.gif',
      'https://media.tenor.com/7RULa0fQYSkAAAAC/celebrate-party.gif',
      'https://media.tenor.com/jwA9f8P6k7gAAAAC/excited-yes.gif',
      'https://media.tenor.com/4tQnQHGhQ0gAAAAC/typing-fast.gif',
    ];

    return fallback.slice(0, limit).map((url) => ({
      media_formats: {
        gif: { url },
      },
    }));
  } catch (error) {
    console.error('Error fetching Tenor GIFs:', error);
    const fallback = [
      'https://media.tenor.com/8M0Kx7Q4GxAAAAAC/happy-dance.gif',
      'https://media.tenor.com/bm8Q6yAlsPsAAAAC/thumbs-up.gif',
      'https://media.tenor.com/7RULa0fQYSkAAAAC/celebrate-party.gif',
      'https://media.tenor.com/jwA9f8P6k7gAAAAC/excited-yes.gif',
      'https://media.tenor.com/4tQnQHGhQ0gAAAAC/typing-fast.gif',
    ];
    return fallback.slice(0, limit).map((url) => ({
      media_formats: {
        gif: { url },
      },
    }));
  }
};

const MAX_IMAGE_SIZE = 8 * 1024 * 1024; // 8 MB
const MAX_IMAGE_DIMENSION = 2000; // px
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate image file type and size
 */
export const validateImageFile = (file) => {
  if (!file) return { valid: false, error: 'No file selected' };
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: 'Unsupported format. Use JPEG, PNG, GIF, or WebP.' };
  }
  if (file.size > MAX_IMAGE_SIZE * 4) {
    return { valid: false, error: `File too large (${formatFileSize(file.size)}). Maximum is 8 MB.` };
  }
  return { valid: true };
};

/**
 * Compress/resize image client-side using Canvas API.
 * Returns a File ≤ 8 MB or throws if impossible.
 * GIFs pass through without re-encoding to preserve animation.
 */
export const compressImage = async (file) => {
  if (file.type === 'image/gif') {
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error(`GIF is ${formatFileSize(file.size)}, exceeds 8 MB limit. Use a smaller GIF.`);
    }
    return file;
  }

  if (file.size <= MAX_IMAGE_SIZE) {
    const img = await loadImageElement(file);
    if (img.width <= MAX_IMAGE_DIMENSION && img.height <= MAX_IMAGE_DIMENSION) {
      return file;
    }
  }

  const img = await loadImageElement(file);
  let { width, height } = img;

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    const ratio = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

  const tryExport = (quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Image compression failed'));
      if (blob.size <= MAX_IMAGE_SIZE) {
        let compressedFile;
        try {
          compressedFile = new File([blob], file.name, { type: outputType });
        } catch (e) {
          compressedFile = blob;
          compressedFile.name = file.name;
        }
        resolve(compressedFile);
      } else if (quality > 0.5) {
        resolve(tryExport(quality - 0.1));
      } else {
        reject(new Error('Could not compress below 8 MB. Try a smaller image.'));
      }
    }, outputType, quality);
  });

  return tryExport(0.85);
};

const loadImageElement = (file) => new Promise(async (resolve, reject) => {
  const errors = [];
  const img = new window.Image();

  // Method 1: URL.createObjectURL (Fastest, avoids memory bloat)
  try {
    await new Promise((res, rej) => {
      const url = URL.createObjectURL(file);
      img.onload = () => { URL.revokeObjectURL(url); res(); };
      img.onerror = () => { URL.revokeObjectURL(url); rej(new Error('URL.createObjectURL onerror')); };
      img.src = url;
    });
    return resolve(img);
  } catch (e) {
    errors.push(`Method 1 (URL): ${e.message}`);
  }

  // Method 2: FileReader (Standard fallback, bypasses blob: CSP blocks)
  try {
    await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error('FileReader result onerror'));
        img.src = e.target.result;
      };
      reader.onerror = (e) => rej(new Error(`FileReader failed to read: ${e.target.error?.message}`));
      reader.readAsDataURL(file);
    });
    return resolve(img);
  } catch (e) {
    errors.push(`Method 2 (FileReader): ${e.message}`);
  }

  // Method 3: Response API (Bypasses FileReader restrictions in WebViews)
  try {
    const buffer = await new Response(file).arrayBuffer();
    // Use chunks to avoid call stack limits on huge files
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += 8192) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
    }
    const base64 = btoa(binary);
    
    await new Promise((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('Response Base64 onerror'));
      img.src = `data:${file.type};base64,${base64}`;
    });
    return resolve(img);
  } catch (e) {
    errors.push(`Method 3 (Response): ${e.message}`);
  }

  // If all methods fail, reject with detailed error
  reject(new Error(`Failed to load image. Details: [${errors.join(' | ')}] (File: ${file.name}, Type: ${file.type}, Size: ${file.size})`));
});

/**
 * Upload image via Hive Images API.
 * Validates, compresses, then delegates to hiveService.uploadImageToHive().
 */
export const uploadImage = async (file, username, hiveServiceInstance) => {
  const validation = validateImageFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const compressed = await compressImage(file);
  return hiveServiceInstance.uploadImageToHive(username, compressed);
};

/**
 * Count characters for limit enforcement.
 * Images and videos count as 1 character each instead of their full shortcode length.
 */
export const countContentCharacters = (markdown) => {
  if (!markdown) return 0;
  let text = String(markdown);
  text = text.replace(/\[img\s+[^\]]+\]/gi, '\u{1F4F7}');
  text = text.replace(/\[video\s+[^\]]+\]/gi, '\u{1F3AC}');
  text = text.replace(/!\[[^\]]*\]\([^)]+\)/g, '\u{1F4F7}');
  text = text.replace(/<img\b[^>]*>/gi, '\u{1F4F7}');
  return text.replace(/\s+/g, ' ').trim().length;
};

const richEditorUtils = {
  getTenorImageUrl,
  fetchVideoTitleFromUrl,
  getVideoTitleFallback,
  getVideoEmbedUrl,
  isImageUrl,
  isImageUrlByContent,
  isValidImageUrl,
  buildCanonicalImageShortcode,
  isValidUrl,
  isValidVideoUrl,
  isVideoUrlByContent,
  htmlToMarkdown,
  markdownToHtml,
  stripHtml,
  countCharacters,
  countContentCharacters,
  formatFileSize,
  fileToBase64,
  searchTenorGifs,
  uploadImage,
  validateImageFile,
  compressImage,
};

export default richEditorUtils;
